import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchHttpError } from '@trade-platform/ui-shared';
import { of } from 'rxjs';
import { concatMap, map, switchMap } from 'rxjs/operators';
import {
    AdminAddBlindCarbonCopyToEnvelopeAction,
    AdminAddBlindCarbonCopyToEnvelopeFailureAction,
    AdminAddBlindCarbonCopyToEnvelopeSuccessAction,
    AdminLoadIfgGatewayFilesAction,
    AdminLoadIfgGatewayFilesFailureAction,
    AdminLoadIfgGatewayFilesSuccessAction,
    AdminLoadOrderQueueOptionsAction,
    AdminLoadOrderQueueOptionsFailureAction,
    AdminLoadOrderQueueOptionsSuccessAction,
    AdminMarkOrderCompleteAction,
    AdminMarkOrderCompleteFailureAction,
    AdminMarkOrderCompleteSuccessAction,
    AdminMoveOrderBackInWorkflowAction,
    AdminMoveOrderBackInWorkflowFailureAction,
    AdminMoveOrderBackInWorkflowSuccessAction,
    AdminMoveOrderForwardInWorkflowAction,
    AdminMoveOrderForwardInWorkflowFailureAction,
    AdminMoveOrderForwardInWorkflowSuccessAction,
    AdminMoveOrderQueueAction,
    AdminMoveOrderQueueFailureAction,
    AdminMoveOrderQueueSuccessAction,
    AdminPushUpdatedFormToOrderAction,
    AdminPushUpdatedFormToOrderFailureAction,
    AdminPushUpdatedFormToOrderSuccessAction,
    AdminRegenerateFileListAction,
    AdminRegenerateFileListFailureAction,
    AdminRegenerateFileListSuccessAction,
    AdminReplaceFormOnOrderAction,
    AdminReplaceFormOnOrderFailureAction,
    AdminReplaceFormOnOrderSuccessAction,
    AdminResubmitFileToIfgGatewayAction,
    AdminResubmitFileToIfgGatewayFailureAction,
    AdminResubmitFileToIfgGatewaySuccessAction,
    AdminResubmitOrderAction,
    AdminResubmitOrderFailureAction,
    AdminResubmitOrderSuccessAction,
    AdminResubmitOrderToIfgGatewayAction,
    AdminResubmitOrderToIfgGatewayFailureAction,
    AdminResubmitOrderToIfgGatewaySuccessAction,
    AdminUpdateEnvelopeRecipientsAction,
    AdminUpdateEnvelopeRecipientsFailureAction,
    AdminUpdateEnvelopeRecipientsSuccessAction,
    AdminUpdateProductProfileDataAction,
    AdminUpdateProductProfileDataFailureAction,
    AdminUpdateProductProfileDataSuccessAction,
    OrderAdminActionTypes,
    OrderNigoToNeedsClarificationAction,
    OrderNigoToNeedsClarificationFailureAction,
    OrderNigoToNeedsClarificationSuccessAction,
    OrderToWetsignAction,
    OrderToWetsignFailureAction,
    OrderToWetsignSuccessAction
} from './actions';
import { OrderAdminService } from './service';

@Injectable()
export class OrderAdminEffects {
    private actions$ = inject(Actions);
    private service = inject(OrderAdminService);

    /** Inserted by Angular inject() migration for backwards compatibility */
    constructor(...args: unknown[]);

    constructor() {}

    convertOrderNigoToNeedsClarification$ = createEffect(() =>
        this.actions$.pipe(
            ofType<OrderNigoToNeedsClarificationAction>(
                OrderAdminActionTypes.ORDER_NIGO_TO_NEEDS_CLARIFICATION
            ),
            switchMap(action =>
                this.service.convertOrderNigoToNeedsClarification(action.payload.orderId).pipe(
                    map(_ => new OrderNigoToNeedsClarificationSuccessAction()),
                    catchHttpError(error =>
                        of(new OrderNigoToNeedsClarificationFailureAction({ error }))
                    )
                )
            )
        )
    );

    convertOrderToWetsign$ = createEffect(() =>
        this.actions$.pipe(
            ofType<OrderToWetsignAction>(OrderAdminActionTypes.ORDER_TO_WETSIGN),
            switchMap(action =>
                this.service.convertOrderToWetsign(action.payload.orderId).pipe(
                    map(_ => new OrderToWetsignSuccessAction()),
                    catchHttpError(error => of(new OrderToWetsignFailureAction({ error })))
                )
            )
        )
    );

    adminMarkOrderComplete$ = createEffect(() =>
        this.actions$.pipe(
            ofType<AdminMarkOrderCompleteAction>(OrderAdminActionTypes.ORDER_MARK_COMPLETE),
            switchMap(action =>
                this.service.adminMarkOrderComplete(action.payload.orderId).pipe(
                    map(_ => new AdminMarkOrderCompleteSuccessAction()),
                    catchHttpError(error => of(new AdminMarkOrderCompleteFailureAction({ error })))
                )
            )
        )
    );

    adminResubmitOrder$ = createEffect(() =>
        this.actions$.pipe(
            ofType<AdminResubmitOrderAction>(OrderAdminActionTypes.ORDER_RESUBMIT),
            switchMap(action =>
                this.service.adminResubmitOrder(action.payload.orderId).pipe(
                    map(_ => new AdminResubmitOrderSuccessAction()),
                    catchHttpError(error => of(new AdminResubmitOrderFailureAction({ error })))
                )
            )
        )
    );

    adminPushUpdatedFormToOrder$ = createEffect(() =>
        this.actions$.pipe(
            ofType<AdminPushUpdatedFormToOrderAction>(
                OrderAdminActionTypes.ORDER_PUSH_UPDATED_FORM
            ),
            switchMap(action =>
                this.service
                    .adminPushUpdatedFormToOrder(action.payload.orderId, action.payload.formId)
                    .pipe(
                        map(_ => new AdminPushUpdatedFormToOrderSuccessAction()),
                        catchHttpError(error =>
                            of(new AdminPushUpdatedFormToOrderFailureAction({ error }))
                        )
                    )
            )
        )
    );

    adminReplaceFormOnOrder$ = createEffect(() =>
        this.actions$.pipe(
            ofType<AdminReplaceFormOnOrderAction>(OrderAdminActionTypes.ORDER_REPLACE_FORM),
            switchMap(action =>
                this.service
                    .adminReplaceFormOnOrder(
                        action.payload.orderId,
                        action.payload.formId,
                        action.payload.file
                    )
                    .pipe(
                        map(_ => new AdminReplaceFormOnOrderSuccessAction()),
                        catchHttpError(error =>
                            of(new AdminReplaceFormOnOrderFailureAction({ error }))
                        )
                    )
            )
        )
    );

    adminMoveOrderBackInWorkflow$ = createEffect(() =>
        this.actions$.pipe(
            ofType<AdminMoveOrderBackInWorkflowAction>(
                OrderAdminActionTypes.ORDER_MOVE_BACK_IN_WORKFLOW
            ),
            switchMap(action =>
                this.service.adminMoveOrderBackInWorkflow(action.payload.orderId).pipe(
                    map(_ => new AdminMoveOrderBackInWorkflowSuccessAction()),
                    catchHttpError(error =>
                        of(new AdminMoveOrderBackInWorkflowFailureAction({ error }))
                    )
                )
            )
        )
    );

    adminMoveOrderForwardInWorkflow$ = createEffect(() =>
        this.actions$.pipe(
            ofType<AdminMoveOrderForwardInWorkflowAction>(
                OrderAdminActionTypes.ORDER_MOVE_FORWARD_IN_WORKFLOW
            ),
            switchMap(action =>
                this.service.adminMoveOrderForwardInWorkflow(action.payload.orderId).pipe(
                    map(_ => new AdminMoveOrderForwardInWorkflowSuccessAction()),
                    catchHttpError(error =>
                        of(new AdminMoveOrderForwardInWorkflowFailureAction({ error }))
                    )
                )
            )
        )
    );

    addBlindCarbonCopyToEnvelope$ = createEffect(() =>
        this.actions$.pipe(
            ofType<AdminAddBlindCarbonCopyToEnvelopeAction>(
                OrderAdminActionTypes.ORDER_ADD_BLIND_CARBON_COPY_TO_ENVELOPE
            ),
            switchMap(action =>
                this.service.adminAddBlindCarbonCopyToEnvelope(action.payload.orderId).pipe(
                    map(_ => new AdminAddBlindCarbonCopyToEnvelopeSuccessAction()),
                    catchHttpError(error =>
                        of(new AdminAddBlindCarbonCopyToEnvelopeFailureAction({ error }))
                    )
                )
            )
        )
    );

    updateProductProfileData$ = createEffect(() =>
        this.actions$.pipe(
            ofType<AdminUpdateProductProfileDataAction>(
                OrderAdminActionTypes.ORDER_UPDATE_PRODUCT_PROFILE_DATA
            ),
            switchMap(action =>
                this.service.adminUpdateProductProfileData(action.payload.orderId).pipe(
                    map(_ => new AdminUpdateProductProfileDataSuccessAction()),
                    catchHttpError(error =>
                        of(new AdminUpdateProductProfileDataFailureAction({ error }))
                    )
                )
            )
        )
    );
    loadIfgGatewayFiles$ = createEffect(() =>
        this.actions$.pipe(
            ofType<AdminLoadIfgGatewayFilesAction>(
                OrderAdminActionTypes.ORDER_LOAD_IFG_GATEWAY_FILES
            ),
            switchMap(action =>
                this.service.adminLoadIfgGatewayFiles(action.payload.orderId).pipe(
                    map(response => new AdminLoadIfgGatewayFilesSuccessAction(response)),
                    catchHttpError(error =>
                        of(new AdminLoadIfgGatewayFilesFailureAction({ error }))
                    )
                )
            )
        )
    );
    resubmitFileToIfgGateway$ = createEffect(() =>
        this.actions$.pipe(
            ofType<AdminResubmitFileToIfgGatewayAction>(
                OrderAdminActionTypes.ORDER_RESUBMIT_FILE_TO_IFG_GATEWAY
            ),
            switchMap(action =>
                this.service
                    .adminResubmitFileToIfgGateway(
                        action.payload.orderId,
                        action.payload.fileTransmissionId
                    )
                    .pipe(
                        map(response => new AdminResubmitFileToIfgGatewaySuccessAction(response)),
                        catchHttpError(error =>
                            of(new AdminResubmitFileToIfgGatewayFailureAction({ error }))
                        )
                    )
            )
        )
    );
    regenerateFileList$ = createEffect(() =>
        this.actions$.pipe(
            ofType<AdminRegenerateFileListAction>(OrderAdminActionTypes.ORDER_REGENERATE_FILE_LIST),
            switchMap(action =>
                this.service.adminRegenerateFileList(action.payload.orderId).pipe(
                    map(response => new AdminRegenerateFileListSuccessAction()),
                    catchHttpError(error => of(new AdminRegenerateFileListFailureAction({ error })))
                )
            )
        )
    );

    adminResubmitOrderToIfgGateway$ = createEffect(() =>
        this.actions$.pipe(
            ofType<AdminResubmitOrderToIfgGatewayAction>(
                OrderAdminActionTypes.ORDER_RESUBMIT_ORDER_TO_IFG_GATEWAY
            ),
            switchMap(action =>
                this.service
                    .adminResubmitOrderToIfgGateway(
                        action.payload.orderId,
                        action.payload.integrationType,
                        action.payload.approvalConfig
                    )
                    .pipe(
                        map(_ => new AdminResubmitOrderToIfgGatewaySuccessAction()),
                        catchHttpError(error =>
                            of(new AdminResubmitOrderToIfgGatewayFailureAction({ error }))
                        )
                    )
            )
        )
    );

    loadOrderQueueOptions$ = createEffect(() =>
        this.actions$.pipe(
            ofType<AdminLoadOrderQueueOptionsAction>(
                OrderAdminActionTypes.ORDER_LOAD_QUEUE_OPTIONS
            ),
            switchMap(action =>
                this.service.adminLoadOrderQueueOptions(action.payload.orderId).pipe(
                    map(response => new AdminLoadOrderQueueOptionsSuccessAction(response)),
                    catchHttpError(error =>
                        of(new AdminLoadOrderQueueOptionsFailureAction({ error }))
                    )
                )
            )
        )
    );

    moveOrderQueue$ = createEffect(() =>
        this.actions$.pipe(
            ofType<AdminMoveOrderQueueAction>(OrderAdminActionTypes.ORDER_MOVE_QUEUE),
            switchMap(action =>
                this.service
                    .adminMoveOrderQueue(action.payload.orderId, action.payload.configId)
                    .pipe(
                        map(_ => new AdminMoveOrderQueueSuccessAction()),
                        catchHttpError(error => of(new AdminMoveOrderQueueFailureAction({ error })))
                    )
            )
        )
    );

    updateEnvelopeRecipients$ = createEffect(() =>
        this.actions$.pipe(
            ofType<AdminUpdateEnvelopeRecipientsAction>(
                OrderAdminActionTypes.ORDER_UPDATE_ENVELOPE_RECIPIENTS
            ),
            // concatMap, not switchMap: each change is an independent append to the order's override
            // list, and the component reloads the order between them. Cancelling an in-flight
            // request would leave a change the admin was told nothing about half-applied.
            concatMap(action =>
                this.service
                    .adminUpdateEnvelopeRecipients(action.payload.orderId, action.payload.change)
                    .pipe(
                        map(_ => new AdminUpdateEnvelopeRecipientsSuccessAction()),
                        catchHttpError(error =>
                            of(new AdminUpdateEnvelopeRecipientsFailureAction({ error }))
                        )
                    )
            )
        )
    );
}
